/**
 * Canvasing API client (reporting + recorded knocks).
 * Avoid duplicate property names in a single interface/object type — they break `tsc` (often from bad merges).
 */
import { authService } from './auth';

const getApiBaseUrl = () => {
  return window.location.origin;
};

const API_BASE_URL = getApiBaseUrl();

export interface RecordedKnock {
  id: number;
  user: number;
  user_name?: string;
  gps_data: Record<string, any>;
  timestamp: string;
  address?: string;
  notes?: string;
  contact_made: boolean;
  interested: boolean;
  inspection?: boolean;
  inspection_scheduled?: boolean;
  left_info?: boolean;
  contact_info_collected?: boolean;
  followup_required: boolean;
  is_marketing_drop?: boolean;
  latitude?: number;
  longitude?: number;
  created_at: string;
  updated_at: string;
}

export interface CanvasingMapData {
  date: string;
  prev_date: string;
  next_date: string | null;
  knocks: Array<{
    id: number;
    lat: number;
    lng: number;
    user: string;
    user_id: number;
    timestamp: string;
    address: string | null;
    contact_made: boolean;
    interested: boolean;
    inspection: boolean;
    inspection_scheduled: boolean;
    left_info: boolean;
    contact_info_collected: boolean;
    followup_required: boolean;
    is_marketing_drop: boolean;
    notes: string | null;
  }>;
  stats: {
    total_knocks: number;
    contact_made: number;
    interested: number;
    inspection: number;
    inspections_scheduled: number;
    left_info: number;
    contact_info_collected: number;
    marketing_drops: number;
    followup_required: number;
    contact_rate: string;
    interest_rate: string;
    user_stats: Array<{
      user_id: number;
      user_name: string;
      knock_count: number;
      contacts_made: number;
      interested: number;
      inspection: number;
      inspections_scheduled: number;
      left_info: number;
      contact_info_collected: number;
      marketing_drops: number;
    }>;
  };
}

export interface CanvasingSummary {
  start_date: string;
  end_date: string;
  daily_stats: Array<{
    date: string;
    total: number;
    contacts: number;
    interested: number;
  }>;
  totals: {
    knocks: number;
    contacts: number;
    interested: number;
    followups: number;
    avg_per_day: number;
  };
}

class CanvasingApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const accessToken = authService.getAccessToken();

    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      ...(options.headers as Record<string, string>),
    };

    const config: RequestInit = {
      ...options,
      headers,
    };

    let response: Response;
    try {
      response = await fetch(url, config);
    } catch (fetchError) {
      console.error(`Canvasing API: Network error for ${endpoint}`);
      throw new Error(`Network error: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
    }

    if (!response.ok) {
      if (response.status === 401) {
        // Try to refresh token
        try {
          await authService.refreshAccessToken();
        } catch {
          authService.clearAccessToken();
          window.location.href = '/login';
          throw new Error('Authentication failed - redirecting to login');
        }
        
        // Retry with new token
        const newAccessToken = authService.getAccessToken();
        const retryConfig: RequestInit = {
          ...config,
          headers: {
            ...headers,
            'Authorization': `Bearer ${newAccessToken}`,
          },
        };
        
        try {
          const retryResponse = await fetch(url, retryConfig);
          
          if (!retryResponse.ok) {
            console.error(`Canvasing API: Retry failed with status ${retryResponse.status} for ${endpoint}`);
            throw new Error(`HTTP error after token refresh! status: ${retryResponse.status}`);
          }
          return retryResponse.json();
        } catch (retryError) {
          console.error(`Canvasing API: Retry request failed for ${endpoint}`);
          throw new Error(`Retry request failed: ${retryError instanceof Error ? retryError.message : 'Unknown error'}`);
        }
      }
      
      console.error(`Canvasing API: HTTP error ${response.status} for ${endpoint}`);
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      // Try to get error details from response
      try {
        const errorData = await response.json();
        if (errorData.detail || errorData.message) {
          errorMessage = errorData.detail || errorData.message;
        }
      } catch {
        // If we can't parse the error response, use the status code
      }
      
      throw new Error(errorMessage);
    }

    try {
      const data = await response.json();
      return data;
    } catch (parseError) {
      console.error(`Canvasing API: Failed to parse JSON response for ${endpoint}`);
      throw new Error(`Failed to parse response: ${parseError instanceof Error ? parseError.message : 'Invalid JSON'}`);
    }
  }

  // Get canvasing map data for a specific date
  async getCanvasingMapData(params?: {
    date?: string; // YYYY-MM-DD format
    user_id?: number;
  }): Promise<CanvasingMapData> {
    const queryParams = new URLSearchParams();
    if (params?.date) {
      queryParams.append('date', params.date);
    }
    if (params?.user_id) {
      queryParams.append('user_id', params.user_id.toString());
    }
    
    const queryString = queryParams.toString();
    const endpoint = `/api/reporting/reports/canvasing-map/${queryString ? `?${queryString}` : ''}`;
    
    return this.request<CanvasingMapData>(endpoint);
  }

  // Get canvasing summary for a date range
  async getCanvasingSummary(params?: {
    start_date?: string; // YYYY-MM-DD format
    end_date?: string; // YYYY-MM-DD format
  }): Promise<CanvasingSummary> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) {
      queryParams.append('start_date', params.start_date);
    }
    if (params?.end_date) {
      queryParams.append('end_date', params.end_date);
    }
    
    const queryString = queryParams.toString();
    const endpoint = `/api/reporting/reports/canvasing-summary/${queryString ? `?${queryString}` : ''}`;
    
    return this.request<CanvasingSummary>(endpoint);
  }

  // Get recorded knocks list
  async getRecordedKnocks(params?: {
    user?: number;
    start_date?: string;
    end_date?: string;
    contact_made?: boolean;
    interested?: boolean;
    followup_required?: boolean;
    is_marketing_drop?: boolean;
    search?: string;
  }): Promise<{ results: RecordedKnock[]; count: number }> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/api/canvasing/recorded-knocks/${queryString ? `?${queryString}` : ''}`;
    
    return this.request<{ results: RecordedKnock[]; count: number }>(endpoint);
  }

  // Create a new knock record
  async createKnock(data: {
    gps_data: Record<string, any>;
    timestamp?: string;
    address?: string;
    notes?: string;
    contact_made?: boolean;
    interested?: boolean;
    followup_required?: boolean;
    is_marketing_drop?: boolean;
  }): Promise<RecordedKnock> {
    return this.request<RecordedKnock>('/api/canvasing/recorded-knocks/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Bulk create knock records
  async bulkCreateKnocks(knocks: Array<{
    gps_data: Record<string, any>;
    timestamp?: string;
    address?: string;
    notes?: string;
    contact_made?: boolean;
    interested?: boolean;
    followup_required?: boolean;
    is_marketing_drop?: boolean;
  }>): Promise<{ message: string; count: number }> {
    return this.request<{ message: string; count: number }>('/api/canvasing/recorded-knocks/bulk-create/', {
      method: 'POST',
      body: JSON.stringify({ knocks }),
    });
  }

  // Get knock statistics
  async getKnockStats(params?: {
    user?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<{
    total_knocks: number;
    contact_made: number;
    interested: number;
    followup_required: number;
    today_knocks: number;
    contact_rate: string;
    interest_rate: string;
    user_stats: Array<any>;
  }> {
    const queryParams = new URLSearchParams();
    
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = `/api/canvasing/recorded-knocks/stats/${queryString ? `?${queryString}` : ''}`;
    
    return this.request(endpoint);
  }
}

export const canvasingApi = new CanvasingApiClient();